import React from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, Building2, Mail, KeyRound } from "lucide-react";
import { api } from "../lib/api";

export default function SubcontractorLoginPage() {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [showReset, setShowReset] = React.useState(false);
  const [resetEmail, setResetEmail] = React.useState("");
  const nav = useNavigate();
  const [params] = useSearchParams();

  React.useEffect(() => {
    if (params.get("invited")) toast.success("Adgangskode sat — log ind med din email + nye kode");
  }, [params]);

  if (localStorage.getItem("ema_sc_token")) return <Navigate to="/vognmand" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/sc/auth/login", { email: email.trim(), password });
      localStorage.setItem("ema_sc_token", data.token);
      localStorage.setItem("ema_sc_company", data.subcontractor.company_name || "");
      toast.success(`Velkommen, ${data.subcontractor.company_name || data.subcontractor.email}`);
      nav("/vognmand");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Forkert email eller adgangskode");
    } finally {
      setBusy(false);
    }
  };

  const requestReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/sc/auth/request-reset", { email: resetEmail.trim() });
      toast.success("Hvis emailen er registreret, har vi sendt et reset-link");
      setShowReset(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke sende reset-link");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-dark flex items-center justify-center p-5" data-testid="sc-login-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 justify-center mb-8">
          <img src="/brand/ema-logo-transparent.png" alt="" className="h-14" />
          <div className="leading-none">
            <div className="font-display font-bold text-2xl text-white">EMA Kurer<span className="text-brand-red">.</span></div>
            <div className="text-[10px] tracking-[0.25em] uppercase font-semibold text-white/50 mt-1">Vognmand-portal</div>
          </div>
        </div>

        {!showReset ? (
          <div className="bg-white p-8">
            <div className="kicker">Login</div>
            <h1 className="font-display font-bold text-2xl text-brand-dark mt-2">Månedlige opgørelser</h1>
            <p className="text-sm text-brand-text2 mt-2">Log ind for at se opgørelser, downloade Excel og uploade jeres faktura.</p>
            <form onSubmit={submit} className="space-y-4 mt-6">
              <div>
                <label className="label-flat">Email</label>
                <div className="relative mt-1">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-text2" />
                  <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    type="email"
                    required
                    autoComplete="email"
                    autoFocus
                    className="input-flat w-full pl-10"
                    data-testid="sc-login-email"
                  />
                </div>
              </div>
              <div>
                <label className="label-flat">Adgangskode</label>
                <div className="relative mt-1">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-text2" />
                  <input
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    type="password"
                    required
                    autoComplete="current-password"
                    className="input-flat w-full pl-10"
                    data-testid="sc-login-password"
                  />
                </div>
              </div>
              <button
                disabled={busy}
                type="submit"
                className="btn-primary w-full inline-flex items-center justify-center gap-2"
                data-testid="sc-login-submit"
              >
                {busy ? <Loader2 className="animate-spin" size={16} /> : <Building2 size={16} />}
                Log ind
              </button>
              <button
                type="button"
                onClick={() => { setShowReset(true); setResetEmail(email); }}
                className="text-xs text-brand-red hover:underline w-full text-center"
                data-testid="sc-forgot-link"
              >
                Glemt adgangskode?
              </button>
            </form>
          </div>
        ) : (
          <div className="bg-white p-8">
            <div className="kicker">Nulstil adgangskode</div>
            <h1 className="font-display font-bold text-2xl text-brand-dark mt-2">Få et reset-link</h1>
            <p className="text-sm text-brand-text2 mt-2">Indtast din email — vi sender et link til at sætte en ny adgangskode.</p>
            <form onSubmit={requestReset} className="space-y-4 mt-6">
              <div>
                <label className="label-flat">Email</label>
                <input
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  type="email"
                  required
                  className="input-flat w-full"
                  data-testid="sc-reset-email"
                />
              </div>
              <button disabled={busy} type="submit" className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="sc-reset-submit">
                {busy ? <Loader2 className="animate-spin" size={16} /> : <KeyRound size={16} />}
                Send reset-link
              </button>
              <button type="button" onClick={() => setShowReset(false)} className="text-xs text-brand-text2 hover:underline w-full text-center">
                Tilbage til login
              </button>
            </form>
          </div>
        )}

        <p className="text-center text-white/40 text-xs mt-6">EMA Kurer ApS · CVR 46465717</p>
      </div>
    </div>
  );
}
